import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCheckCircle, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './AmwayToolPage.css';

const AmwayToolPage = () => {
  const { getAccessToken } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const [selectedIbo, setSelectedIbo] = useState('');

  const [validateData, setValidateData] = useState(null);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateError, setValidateError] = useState(null);

  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState(null);
  const [kpiPeriod, setKpiPeriod] = useState('');

  const debounceTimer = useRef(null);

  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/tickets/users/search?search=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();
      if (response.ok && result.status === 'success') {
        setSearchResults(result.data || []);
        setShowDropdown(true);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setSearchLoading(false);
    }
  }, [getAccessToken]);

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    setSelectedIbo('');
    setValidateData(null);
    setValidateError(null);
    setKpiData(null);
    setKpiError(null);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  const selectUser = (user) => {
    setSearchQuery(`${user.iboNumber} - ${user.iboName || ''}`);
    setSelectedIbo(user.iboNumber);
    setShowDropdown(false);
    setSearchResults([]);
    setValidateData(null);
    setValidateError(null);
    setKpiData(null);
    setKpiError(null);
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);

      if (!selectedIbo && searchQuery) {
        const possibleIbo = searchQuery.trim();
        if (/^\d+$/.test(possibleIbo)) {
          setSelectedIbo(possibleIbo);
        }
      }
    }, 200);
  };

  const handleLookup = async () => {
    const iboNumber = selectedIbo || searchQuery.trim();
    if (!iboNumber) return;

    if (/^\d+$/.test(iboNumber)) {
      setSelectedIbo(iboNumber);
    } else {
      return;
    }

    setValidateLoading(true);
    setValidateError(null);
    setValidateData(null);

    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/amway-validate-info?iboNumber=${encodeURIComponent(iboNumber)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();
      if (response.ok && result.status === 'success' && result.data) {
        setValidateData(result.data);
      } else {
        setValidateError(result.message || 'Failed to fetch validation info');
      }
    } catch (err) {
      console.error('Error fetching validate info:', err);
      setValidateError('Failed to fetch validation info. Please try again.');
    } finally {
      setValidateLoading(false);
    }
  };

  const handleKpiLookup = async () => {
    const iboNumber = selectedIbo || searchQuery.trim();
    if (!iboNumber || !/^\d+$/.test(iboNumber)) return;

    setKpiLoading(true);
    setKpiError(null);
    setKpiData(null);

    try {
      const token = await getAccessToken();
      if (!token) return;

      let url = `${config.API_BASE_URL}/um/v1/admin/user/amway-kpi-info?iboNumber=${encodeURIComponent(iboNumber)}`;
      if (kpiPeriod) {
        url += `&period=${encodeURIComponent(kpiPeriod)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (response.ok && result.status === 'success' && result.data) {
        setKpiData(result.data);
      } else {
        setKpiError(result.message || 'Failed to fetch KPI info');
      }
    } catch (err) {
      console.error('Error fetching KPI info:', err);
      setKpiError('Failed to fetch KPI info. Please try again.');
    } finally {
      setKpiLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setShowDropdown(false);
      handleLookup();
    }
  };

  const currentIbo = selectedIbo || (searchQuery.trim() && /^\d+$/.test(searchQuery.trim()) ? searchQuery.trim() : '');

  return (
    <div className="amway-tool-container">
      <div className="amway-tool-content">
        <h2>Amway Tool</h2>
        <p className="amway-tool-subtitle">Look up IBO validation and KPI information from Amway</p>

        {/* IBO Search Section */}
        <div className="amway-tool-search-section">
          <label className="amway-tool-label">IBO Number / Name</label>
          <div className="amway-tool-search-row">
            <div className="autocomplete-container">
              <input
                type="text"
                placeholder="Search by IBO# or name..."
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                onBlur={handleSearchBlur}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true);
                  }
                }}
                className={validateError ? 'input-error' : validateData ? 'input-valid' : ''}
              />
              {searchLoading && <span className="search-spinner"></span>}
              {validateData && !searchLoading && (
                <FontAwesomeIcon icon={faCheckCircle} className="validation-icon valid" />
              )}
              {validateError && !searchLoading && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="validation-icon invalid" />
              )}
              {showDropdown && searchResults.length > 0 && (
                <div className="autocomplete-dropdown">
                  {searchResults.map((user, idx) => (
                    <div
                      key={idx}
                      className="autocomplete-item"
                      onMouseDown={() => selectUser(user)}
                    >
                      <span className="autocomplete-ibo">{user.iboNumber}</span>
                      <span className="autocomplete-name">{user.iboName || 'N/A'}</span>
                      {user.teamName && (
                        <span className="autocomplete-team">{user.teamName}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              className="amway-tool-lookup-btn"
              onClick={handleLookup}
              disabled={!currentIbo || validateLoading}
            >
              {validateLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
              <span>Validate IBO</span>
            </button>
          </div>
        </div>

        {/* Validate IBO Results */}
        {validateLoading && (
          <div className="amway-tool-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Fetching validation info...</p>
          </div>
        )}

        {validateError && (
          <div className="amway-tool-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{validateError}</span>
          </div>
        )}

        {validateData && (
          <div className="amway-tool-result-card">
            <h3>Amway Validate IBO Result</h3>
            <div className="amway-tool-result-grid">
              <div className="result-item">
                <span className="result-label">IBO Number</span>
                <span className="result-value">{validateData.ibo_number || validateData.iboNumber || '-'}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Status</span>
                <span className={`result-value result-badge ${validateData.status === 'Active' ? 'badge-success' : 'badge-warning'}`}>
                  {validateData.status || '-'}
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">Payment Eligible</span>
                <span className={`result-value result-badge ${validateData.payment_eligible === 'Yes' || validateData.paymentEligible === 'Yes' ? 'badge-success' : 'badge-warning'}`}>
                  {validateData.payment_eligible || validateData.paymentEligible || '-'}
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">Consent Provided</span>
                <span className={`result-value result-badge ${validateData.consent_provided === 'Yes' || validateData.consentProvided === 'Yes' ? 'badge-success' : 'badge-warning'}`}>
                  {validateData.consent_provided || validateData.consentProvided || '-'}
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">Application Date</span>
                <span className="result-value">{validateData.application_date || validateData.applicationDate || '-'}</span>
              </div>
              <div className="result-item">
                <span className="result-label">First Contract Year</span>
                <span className={`result-value result-badge ${validateData.firstContractYear ? 'badge-info' : 'badge-neutral'}`}>
                  {validateData.firstContractYear ? 'Yes' : 'No'}
                </span>
              </div>
              {validateData.message && (
                <div className="result-item result-full-width">
                  <span className="result-label">Message</span>
                  <span className="result-value">{validateData.message}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* KPI Info Section */}
        {currentIbo && (
          <div className="amway-tool-kpi-section">
            <h3>KPI Information</h3>
            <div className="amway-tool-kpi-controls">
              <div className="kpi-period-field">
                <label>Period (YYYYMM)</label>
                <input
                  type="text"
                  placeholder="e.g. 202604 (optional)"
                  value={kpiPeriod}
                  onChange={(e) => setKpiPeriod(e.target.value)}
                  maxLength={6}
                />
              </div>
              <button
                className="amway-tool-kpi-btn"
                onClick={handleKpiLookup}
                disabled={kpiLoading}
              >
                {kpiLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faSearch} />
                )}
                <span>Get KPI Info</span>
              </button>
            </div>

            {kpiLoading && (
              <div className="amway-tool-loading">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p>Fetching KPI info...</p>
              </div>
            )}

            {kpiError && (
              <div className="amway-tool-error">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{kpiError}</span>
              </div>
            )}

            {kpiData && (
              <div className="amway-tool-result-card kpi-card">
                <h3>Amway KPI Result</h3>
                <div className="amway-tool-result-grid">
                  <div className="result-item">
                    <span className="result-label">IBO Name</span>
                    <span className="result-value">{kpiData.ibo_name || kpiData.iboName || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Application Date</span>
                    <span className="result-value">{kpiData.application_date || kpiData.applicationDate || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Current Award</span>
                    <span className="result-value">{kpiData.current_award || kpiData.currentAward || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Current Award Qualified Period</span>
                    <span className="result-value">{kpiData.current_award_qualified_period || kpiData.currentAwardQualifiedPeriod || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Highest Award</span>
                    <span className="result-value">{kpiData.highest_award || kpiData.highestAward || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Highest Award Qualified Period</span>
                    <span className="result-value">{kpiData.highest_award_qualified_period || kpiData.highestAwardQualifiedPeriod || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Current GAR Award</span>
                    <span className="result-value">{kpiData.current_gar_award || kpiData.currentGarAward || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Current GAR Qualified Period</span>
                    <span className="result-value">{kpiData.current_gar_award_qualified_period || kpiData.currentGarAwardQualifiedPeriod || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Highest GAR Award</span>
                    <span className="result-value">{kpiData.highest_gar_award || kpiData.highestGarAward || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Highest GAR Qualified Period</span>
                    <span className="result-value">{kpiData.highest_gar_award_qualified_period || kpiData.highestGarAwardQualifiedPeriod || '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Personal PV</span>
                    <span className="result-value">{kpiData.personal_pv_value ?? kpiData.personalPvValue ?? '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Group PV</span>
                    <span className="result-value">{kpiData.group_pv_value ?? kpiData.groupPvValue ?? '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">VCS PV</span>
                    <span className="result-value">{kpiData.vcs_pv_value ?? kpiData.vcsPvValue ?? '-'}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">VCS Percentage</span>
                    <span className="result-value">
                      {(kpiData.vcs_percentage ?? kpiData.vcsPercentage) != null
                        ? `${kpiData.vcs_percentage ?? kpiData.vcsPercentage}%`
                        : '-'}
                    </span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Upline Platinum IBO</span>
                    <span className="result-value">{kpiData.upline_platinum_ibo || kpiData.uplinePlatinumIbo || '-'}</span>
                  </div>
                  {(kpiData.message || kpiData.message === '') && kpiData.message !== null && (
                    <div className="result-item result-full-width">
                      <span className="result-label">Message</span>
                      <span className="result-value">{kpiData.message || '-'}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AmwayToolPage;
